/**
 * THE UPSCALE SEAM — stages D and E of `docs/ai/plans/dynamic-resolution.md`.
 *
 * ── WHAT THIS IS, ARCHITECTURALLY (rule 0) ──────────────────────────────────────────────────
 * Stage B split ONE knob into two levers: OUTPUT resolution (what the compositor presents, what
 * every post pass costs) and RENDER resolution (output × renderScale, what the SCENE costs).
 * Stage C built a controller that drives the second one. Both shipped with the two levers
 * multiplied back together into a single `setPixelRatio`, because with no post chain there is
 * nowhere else for the render lever to live.
 *
 * This module is that "nowhere else". It is a STRATEGY TABLE, not a feature: one named upscale
 * mode per row, each declaring **which lever it owns** and **what it costs**, and `scene.ts`
 * asks the handle rather than branching on the mode name. `bilinear` — today's behaviour, zero
 * passes, no `RenderPipeline` at all — is a first-class row in that table and the honest floor,
 * not a fallback hack. That matters because the tier-0 answer may well BE `bilinear` (see
 * "the HalfFloat tax" below) and a design in which the good outcome is a special case buries it.
 *
 * WHERE IT BELONGS: `src/render/` — may import three, never entities, never the sim. It knows
 * about a renderer, a scene, a camera and a number. It does NOT know about tiers, URL params or
 * tuning JSON; `scene.ts` resolves the mode and hands it in, exactly as it already does for the
 * resolution controller.
 *
 * WHICH SEAM IT CROSSES: exactly one, deliberately. `scene.ts` owns the renderer and the ONE
 * place the drawing buffer is sized (`applyResolution`). This module takes over `render()` and
 * the render half of `applyResolution`; nothing else in the codebase changes, because the DOM UI
 * is a z-2000 overlay that never sees the drawing buffer.
 *
 * WHAT IT MAKES HARDER: (1) the scene no longer draws to the canvas directly, so anything that
 * wants to read back a frame, or to render a second camera, has to go through the pipeline;
 * (2) `renderer.info.drawCalls` gains the post quads, so the number is no longer "meshes";
 * (3) `taau` flips `needsPreviousData()` on every material in the scene — a global recompile and
 * a permanent per-vertex tax. That last one is why the mode is DATA and not a default.
 *
 * HOW WE KNOW IT STOPPED WORKING: `window.__fwg.state().resolution.pipeline` reports the mode,
 * the pass counts and the scene pass's real size, so the browser tier asserts the shape through
 * STATE (repo law: no 3D pixel baselines). `tests/pipeline.test.ts` pins the pure parts.
 *
 * ── THE THREE LANDMINES THAT SHAPED THE TABLE ───────────────────────────────────────────────
 *
 * 1. **`SharpenNode` IS NOT AN UPSCALER, AND USING IT AS ONE IS WORSE THAN BILINEAR.** Its RCAS
 *    body fetches with `textureLoad(inputTex, floor(uv × textureSize(inputTex)))` — INPUT-texel
 *    granularity — while its render target is sized to the DRAWING BUFFER. Point it at a
 *    half-res scene pass and you get a NEAREST-NEIGHBOUR magnification whose blocks RCAS then
 *    sharpens. That is `sharpen-out` below; it exists so the claim is measured rather than
 *    asserted, and it is not a shipping candidate.
 *    The useful arrangement is the other one: leave the pass at 1.0 of a DRAWING BUFFER that is
 *    itself small (the stage-B0 lever), sharpen 1:1 at render resolution, and let the compositor
 *    magnify an already-sharpened image. Two cheap passes, no output-resolution cost at all.
 *    That is `sharpen`, and it is the only mode here whose cost does not scale with output.
 *
 * 2. **THE HALFFLOAT TAX.** `SharpenNode`, `FSR1Node` and `TAAUNode` all hard-code
 *    `type: HalfFloatType` on their internal render targets and expose no way to change it.
 *    Our tier-0 decision is `UnsignedByteType`, for exactly the bandwidth reason. The SCENE pass
 *    is fine — `PassNode.setup()` does `renderTarget.texture.type = renderer.getOutputBufferType()`
 *    — but every upscaler target downstream of it is 16-bit whether tier 0 wants it or not. On a
 *    bandwidth-starved tablet this can eat the whole win; if it does, `bilinear` is the row that
 *    wins, and that is a perfectly good outcome.
 *
 * 3. **MSAA IS PER-PASS HERE, NOT PER-RENDERER.** `PassNode.setup()` does
 *    `renderTarget.samples = options.samples ?? renderer.samples`, so `antialias: true` silently
 *    MSAAs the scene pass — which three's own TAAU docs forbid. `samples: 0` in the pass options
 *    turns it off for the SCENE PASS ONLY, without touching the constructor flag that is the
 *    owner's look call. `taau` forces it; every other mode inherits, so `?antialias=0` still
 *    prices MSAA exactly the way stage A priced it.
 */
import * as THREE from 'three/webgpu';
import { mrt, output, pass, velocity } from 'three/tsl';
/*
 * EXPLICIT PATHS, not the `three/addons` barrel. `examples/jsm/Addons.js` re-exports the whole
 * example library; reaching the display nodes through it would defeat tree-shaking and drag
 * every other post node into the eager main chunk. These three files are the entire cost.
 */
import { fsr1 } from 'three/addons/tsl/display/FSR1Node.js';
import { sharpen } from 'three/addons/tsl/display/SharpenNode.js';
import { taau } from 'three/addons/tsl/display/TAAUNode.js';
import { clampPassScale, type UpscaleMode } from './upscale';

export type { UpscaleMode } from './upscale';

/** What `__fwg.state().resolution.pipeline` reports. Flat and JSON-serializable. */
export interface PipelineState {
  mode: UpscaleMode;
  /** false ⇒ the render scale rides `setPixelRatio` (stage B0); true ⇒ `PassNode` owns it. */
  ownsRenderScale: boolean;
  /** Full-screen passes added on top of `renderer.render`, at OUTPUT resolution. */
  outputPasses: number;
  /** Full-screen passes added on top of `renderer.render`, at RENDER resolution. */
  renderPasses: number;
  /** The scene pass's actual render-target size, or null for `bilinear`. */
  inputWidth: number | null;
  inputHeight: number | null;
  /** The scale actually handed to `PassNode.setResolutionScale`, after the ≥1px clamp. */
  passScale: number;
  /** `renderTarget.samples` on the SCENE pass — the MSAA that actually happened. */
  sceneSamples: number;
  /** True while the chain is skipped entirely because the render scale is native (see below). */
  bypassed: boolean;
}

export interface RenderPipelineHandle {
  readonly mode: UpscaleMode;
  /**
   * **Which lever this mode owns.** `false` means the caller must fold `renderScale` into
   * `setPixelRatio` (stage B0 — the drawing buffer itself shrinks); `true` means the caller
   * sizes the drawing buffer at OUTPUT resolution and this handle shrinks the scene pass.
   * `scene.ts` reads this instead of branching on the mode name.
   */
  readonly ownsRenderScale: boolean;
  /** Called from `applyResolution` AFTER the drawing buffer has been sized. */
  setRenderScale(scale: number): void;
  render(): void;
  state(): PipelineState;
  dispose(): void;
}

/** `bilinear`: no pipeline, no post, today's behaviour. A row, not an absence. */
function bilinearHandle(
  renderer: THREE.WebGPURenderer,
  scene: THREE.Scene,
  camera: THREE.Camera,
): RenderPipelineHandle {
  return {
    mode: 'bilinear',
    ownsRenderScale: false,
    setRenderScale() {
      /* the caller already folded it into the pixel ratio */
    },
    render() {
      renderer.render(scene, camera);
    },
    state() {
      return {
        mode: 'bilinear',
        ownsRenderScale: false,
        outputPasses: 0,
        renderPasses: 0,
        inputWidth: null,
        inputHeight: null,
        passScale: 1,
        sceneSamples: renderer.samples,
        bypassed: true,
      };
    },
    dispose() {
      /* nothing owned */
    },
  };
}

/**
 * Build a node post chain for one of the upscaling modes.
 *
 * `mode` is resolved by the caller (tier default + `?upscale=` override) — this module never
 * reads the URL or the tuning table, the same discipline `resolution.ts` keeps.
 */
export function createRenderPipeline(
  renderer: THREE.WebGPURenderer,
  scene: THREE.Scene,
  camera: THREE.PerspectiveCamera,
  mode: UpscaleMode,
): RenderPipelineHandle {
  if (mode === 'bilinear') return bilinearHandle(renderer, scene, camera);

  /*
   * TAAU is the ONE mode that must not see MSAA (three's own node docs say so: resolving a
   * multisampled buffer defeats the sub-pixel jitter the whole algorithm is built on). Every
   * other mode inherits `renderer.samples`, so the `antialias` constructor flag — the owner's
   * look call — keeps meaning what it meant in stage A, and `?antialias=0` still prices it.
   */
  const scenePass = mode === 'taau' ? pass(scene, camera, { samples: 0 }) : pass(scene, camera);
  scenePass.name = 'scene';

  const pipeline = new THREE.RenderPipeline(renderer);

  let passScale = 1;
  const ownsRenderScale = mode !== 'sharpen';

  if (mode === 'sharpen' || mode === 'sharpen-out') {
    /*
     * RCAS only. `sharpen` leaves the pass at 1.0 (the DRAWING BUFFER is what shrank) so RCAS
     * runs 1:1 at render resolution; `sharpen-out` drives the pass scale so RCAS runs at output
     * resolution over a nearest-magnified input — landmine 1, kept measurable.
     *
     * `sharpness` 0.2 and `denoise` false are the node's own defaults, spelled out because they
     * are the two knobs anyone reaches for after looking at the pictures (0 = maximum
     * sharpening, 2 = none).
     */
    pipeline.outputNode = sharpen(scenePass, 0.2, false);
  } else if (mode === 'fsr1') {
    /*
     * EASU + RCAS. Fed the RAW pass texture, deliberately: `convertToTexture` passes a PassNode
     * through untouched, but wrapping it in anything (`renderOutput`, `fxaa`) makes it a plain
     * node, which `convertToTexture` then materialises as an auto-resizing RTT at DRAWING BUFFER
     * size — a whole extra output-resolution pass, and it would bilinearly upscale the image
     * BEFORE EASU ever saw it, which is the one thing EASU exists to avoid.
     *
     * The price of that: FSR 1 runs in LINEAR working space, before ACES and before the sRGB
     * transfer (the pipeline's `outputColorTransform` applies both on the final quad). AMD's
     * shader assumes perceptual, [0,1] input. In practice RCAS's `hitMax` limiter goes
     * non-negative above 1 and the `min(…, 0)` clamp then zeroes the lobe, so HDR highlights
     * simply do not get sharpened rather than ringing; EASU is tolerant. Measured, not assumed
     * — see the D/E-RESULTS table in the plan.
     */
    pipeline.outputNode = fsr1(scenePass, 0.2, false);
  } else {
    /*
     * TAAU. The bill, itemised in plan §4, is real and all of it is paid here:
     *
     *   - `mrt({ output, velocity })` flips `needsPreviousData()` on the renderer, adding
     *     previous-position vertex work to EVERY material in the scene (a global recompile and a
     *     permanent per-vertex tax: skinning runs twice per vertex, and our re-posed
     *     `InstancedMesh` platforms need a full previous-matrix buffer copy per frame plus
     *     doubled instance VRAM).
     *   - `BatchedMesh` has NO motion-vector support upstream. A4's planned BatchedMesh decor
     *     would get silently WRONG velocities under this mode. Not solved here; logged.
     *   - The camera is JITTERED. `TAAUNode` brackets it — `setViewOffset` from the pipeline's
     *     `onBeforeRenderPipeline`, `clearViewOffset` from `onAfterRenderPipeline` — so anything
     *     reading `camera.projectionMatrix` OUTSIDE `render()` (the editor's NDC picking,
     *     `raycastGround`, the predicted-jump-arc math) reads the UNJITTERED matrix. That
     *     bracket is what makes the audit come out clean; it is a property of the NODE, not of
     *     our code, and it would break the moment anything read the matrix from inside another
     *     node's `updateBefore`.
     */
    scenePass.setMRT(mrt({ output, velocity }));
    pipeline.outputNode = taau(
      scenePass.getTextureNode('output'),
      scenePass.getTextureNode('depth'),
      scenePass.getTextureNode('velocity'),
      camera,
    );
  }

  /*
   * Pass accounting, stated rather than guessed, because the plan's whole cost argument turns on
   * WHICH RESOLUTION each pass runs at:
   *   sharpen      → scene RT (render) + RCAS RT (render) + output quad (render)  = 2 extra, cheap
   *   sharpen-out  → scene RT (render) + RCAS RT (output) + output quad (output)  = 2 extra, dear
   *   fsr1         → scene RT (render) + EASU (output) + RCAS (output) + quad     = 3 extra, dear
   *   taau         → scene RT (render) + resolve (output) + quad, plus two copies = 2 extra, dear
   * `bilinear` draws the scene straight to the canvas, so it pays none of them — including the
   * final output quad every pipeline mode has to pay just to composite.
   */
  const outputPasses = mode === 'fsr1' ? 3 : mode === 'taau' ? 2 : mode === 'sharpen-out' ? 2 : 0;
  const renderPasses = mode === 'sharpen' ? 2 : 0;
  const size = new THREE.Vector2();

  /*
   * ── AT NATIVE SCALE THE SPATIAL CHAINS ARE PURE TAX, SO THEY ARE SKIPPED ────────────────────
   *
   * `sharpen`, `sharpen-out` and `fsr1` exist for exactly one purpose: to undo the blur of
   * rendering below the output resolution. At renderScale 1.0 there is no blur to undo, and the
   * chain becomes a full-screen pass (or three) that changes almost nothing and costs real
   * milliseconds — MEASURED, on the owner's GPU, at 1600x900 DPR 2: `sharpen` +10.9 ms and
   * `fsr1` +25.4 ms on the WebGL2 fallback, +3.0 / +15.3 ms on WebGPU. Shipping a default that
   * charges a player with a fast machine 10 ms to sharpen an image that is already sharp would
   * be indefensible, and it is what turns `sharpen` from an experiment into a shippable default:
   * free when the controller is at the top rung, cheap when it is not.
   *
   * `taau` is excluded because it is not only an upscaler — at 1.0 it is temporal AA in its own
   * right, and bypassing it would drop its history and change the look rather than preserve it.
   *
   * The bypass is REPORTED (`state().bypassed`), never silent: "the chain did nothing" and "the
   * chain is broken" must not look alike from the outside.
   */
  const bypassAtNative = mode !== 'taau';
  let bypassed = false;

  return {
    mode,
    ownsRenderScale,
    setRenderScale(scale) {
      bypassed = bypassAtNative && scale >= 1;
      if (!ownsRenderScale) return;
      renderer.getDrawingBufferSize(size);
      passScale = clampPassScale(scale, size.x, size.y);
      scenePass.setResolutionScale(passScale);
    },
    render() {
      if (bypassed) renderer.render(scene, camera);
      else pipeline.render();
    },
    state() {
      const rt = scenePass.renderTarget;
      return {
        mode,
        ownsRenderScale,
        outputPasses,
        renderPasses,
        inputWidth: rt.width,
        inputHeight: rt.height,
        passScale,
        sceneSamples: rt.samples,
        bypassed,
      };
    },
    dispose() {
      pipeline.dispose();
      scenePass.dispose();
    },
  };
}
